import {
  type FC,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { type ChangePromptData, LLMPrompt } from './LLMPrompt';
import { LLMProviderSelect } from './LLMProviderSelect';
import { StyledLoader } from './styles';
import type {
  LLMDecisionData,
  LLMErrors,
  MergeFieldValidationOption,
  ModelOption,
} from './types';
import { LLMMergeFieldValidation } from './LLMMergeFieldValidation';
import { useProviderOptions } from './useProviderOptions';
import { LLMConfidenceThreshold } from './LLMConfidenceThreshold';
import type { WYSIWYGHandle } from 'components/WYSIWYG/types';

type LLMDecisionProps = {
  llmDecision: LLMDecisionData;
  renderAIComponent?: (props: {
    onChange: (data: Partial<LLMDecisionData>) => void;
    editorRef: MutableRefObject<WYSIWYGHandle | null>;
  }) => ReactNode;
  mergeFields: string[];
  onChange: (data: Partial<LLMDecisionData>) => void;
  errors: LLMErrors;
};

export const LLMDecision: FC<LLMDecisionProps> = ({
  llmDecision: {
    modelName,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    confidenceThreshold,
    belowThresholdAction,
    modelJustification,
  },
  renderAIComponent,
  errors,
  mergeFields,
  onChange,
}) => {
  const { t } = useTranslation();

  const { isProvidersLoading, providerOptions, model } = useProviderOptions({
    modelName,
    isForDecision: true,
  });

  const setModelName = useCallback(
    (model: ModelOption | undefined) => {
      onChange({
        modelName: model?.value,
        businessPluginAppId: model?.model?.business_plugin_app_id,
        modelJustification: '',
      });
    },
    [onChange]
  );

  const onChangePrompt = useCallback(
    ({ htmlPrompt, prompt }: ChangePromptData) => {
      onChange({
        htmlPrompt,
        prompt,
      });
    },
    [onChange]
  );

  const onChangeMergeFieldValidation = useCallback(
    ({ value }: MergeFieldValidationOption) => {
      onChange({
        mergeFieldValidation: value,
      });
    },
    [onChange]
  );

  const editorRef = useRef<WYSIWYGHandle>(null);

  return (
    <StyledLoader loading={isProvidersLoading}>
      {renderAIComponent?.({ onChange, editorRef })}
      <LLMProviderSelect
        value={model}
        onChange={setModelName}
        options={providerOptions}
        errors={errors}
        disabled={false}
        modelJustification={modelJustification}
      />
      <LLMPrompt
        prompt={prompt}
        htmlPrompt={htmlPrompt}
        mergeFields={mergeFields}
        model={model}
        noProviderOptions={!providerOptions.length}
        disabled={!model}
        onChange={onChangePrompt}
        placeholder={t(
          'Based on context, is the customer showing purchase intent?'
        )}
        isDecision
        editorRef={editorRef}
      />
      <LLMMergeFieldValidation
        value={mergeFieldValidation}
        onChange={onChangeMergeFieldValidation}
        disabled={!model}
      />
      <LLMConfidenceThreshold
        confidenceThreshold={confidenceThreshold}
        belowThresholdAction={belowThresholdAction}
        onChange={onChange}
        disabled={!model}
      />
    </StyledLoader>
  );
};
